import { useMemo, useState, useEffect } from 'react';
import UseArchivistPagination from '@hooks/api/queries/archivist/useArchivistGetPagination';
import QueryKeys from '@src/constants/queryKeys';
import {
  ArchivePoint,
  ArchivistPaginationResponse,
} from '@src/types/Archivist';
import { useDataPointSearchState } from '../context/DataPointSearchContext';
import { useCascadingFilters } from './useCascadingFilters';
import { gridFiltersToFilterObjects, mergeFilters } from '../utils';

interface UseDataPointsSearchReturn {
  rows: ArchivePoint[];
  totalRowCount: number;
  isLoading: boolean;
}

export const useDataPointsSearch = (
  isModalOpen: boolean,
): UseDataPointsSearchReturn => {
  const state = useDataPointSearchState();
  const { activeFilters } = useCascadingFilters();

  const [rows, setRows] = useState<ArchivePoint[]>([]);
  const [totalRowCount, setTotalRowCount] = useState<number>(0);

  const mergedFilters = useMemo(() => {
    const gridFilterObjects = gridFiltersToFilterObjects(state.gridFilters);
    return mergeFilters(activeFilters, gridFilterObjects);
  }, [activeFilters, state.gridFilters]);

  const queryParams = useMemo(
    () => ({
      page: state.pagination.page + 1,
      page_size: state.pagination.pageSize,
      filters: mergedFilters,
      ...(state.activeSearch && { search: state.activeSearch }),
    }),
    [
      state.pagination.page,
      state.pagination.pageSize,
      mergedFilters,
      state.activeSearch,
    ],
  );

  const queryOptions = useMemo(
    () => ({
      enabled: isModalOpen,
      queryKeys: [
        QueryKeys['get-pagination'],
        'dashboard-datapoint-search-v2',
        state.pagination.page.toString(),
        state.pagination.pageSize.toString(),
        JSON.stringify(mergedFilters),
        state.activeSearch || '',
      ],
    }),
    [
      isModalOpen,
      state.pagination.page,
      state.pagination.pageSize,
      mergedFilters,
      state.activeSearch,
    ],
  );

  const { data, isLoading } = UseArchivistPagination({
    params: queryParams,
    options: queryOptions,
  }) as {
    data: ArchivistPaginationResponse | undefined;
    isLoading: boolean;
  };

  useEffect(() => {
    if (data?.data && Array.isArray(data.data)) {
      setRows(data.data);
      setTotalRowCount(data.total_count || 0);
    } else {
      setRows([]);
      setTotalRowCount(0);
    }
  }, [data]);

  return {
    rows,
    totalRowCount,
    isLoading,
  };
};

export default useDataPointsSearch;
